/**
 * Custom confirm/alert dialogs to replace the browser-native `confirm()`
 * and `alert()` calls. Matches Lattice's pattern: a Provider holds the
 * pending request, components call `useConfirm()` / `useAlert()` to get
 * an async function that returns a Promise<boolean | void> once the user
 * picks.
 *
 * Styling matches the other overlay cards (ivory bg, dashed-rule chrome,
 * accent + warn button variants).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

interface ConfirmOptions {
  readonly title?: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /** Renders the confirm button in warn/danger style. Use for destructive
   *  actions (reset progress, wipe a solve, etc.). */
  readonly danger?: boolean;
}

interface AlertOptions {
  readonly title?: string;
  readonly message: string;
  readonly okLabel?: string;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
type AlertFn = (opts: AlertOptions) => Promise<void>;

interface DialogCtx {
  readonly confirm: ConfirmFn;
  readonly alertModal: AlertFn;
}

const Ctx = createContext<DialogCtx | null>(null);

// Internal state shape — one pending request at a time. Sequential dialogs
// are fine for this app's scope; if we ever need stacked dialogs, this
// becomes a queue.
type PendingRequest =
  | {
      kind: 'confirm';
      opts: ConfirmOptions;
      resolve: (v: boolean) => void;
    }
  | {
      kind: 'alert';
      opts: AlertOptions;
      resolve: () => void;
    };

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingRequest | null>(null);

  const confirm: ConfirmFn = useCallback(
    (opts) =>
      new Promise<boolean>((resolve) => {
        setPending({ kind: 'confirm', opts, resolve });
      }),
    []
  );

  const alertModal: AlertFn = useCallback(
    (opts) =>
      new Promise<void>((resolve) => {
        setPending({ kind: 'alert', opts, resolve });
      }),
    []
  );

  const closeWith = (value: boolean | void) => {
    if (!pending) return;
    if (pending.kind === 'confirm') pending.resolve(value as boolean);
    else pending.resolve();
    setPending(null);
  };

  // Escape closes (cancels for confirm, dismisses for alert)
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeWith(pending.kind === 'confirm' ? false : undefined);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  return (
    <Ctx.Provider value={{ confirm, alertModal }}>
      {children}
      {pending && <Modal pending={pending} closeWith={closeWith} />}
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmDialogProvider');
  return ctx.confirm;
}

export function useAlert(): AlertFn {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAlert must be used within ConfirmDialogProvider');
  return ctx.alertModal;
}

function Modal({
  pending,
  closeWith,
}: {
  pending: PendingRequest;
  closeWith: (v: boolean | void) => void;
}) {
  const isConfirm = pending.kind === 'confirm';
  const opts = pending.opts;
  const dangerClass =
    isConfirm && (pending.opts as ConfirmOptions).danger ? ' danger' : '';

  return (
    <div className="overlay" id="confirmOverlay">
      <div
        className="overlayScrim"
        // Tap-on-scrim cancels (confirm) or dismisses (alert)
        onClick={() => closeWith(isConfirm ? false : undefined)}
      />
      <div className="overlayCard confirmCard" role="dialog" aria-modal="true">
        {opts.title && (
          <header>
            <h2>{opts.title}</h2>
          </header>
        )}
        <p className="confirmMessage">{opts.message}</p>
        <div className="confirmActions">
          {isConfirm ? (
            <>
              <button
                className="ghost"
                onClick={() => closeWith(false)}
              >
                {(pending.opts as ConfirmOptions).cancelLabel ?? 'Cancel'}
              </button>
              <button
                className={`confirmPrimary${dangerClass}`}
                onClick={() => closeWith(true)}
              >
                {(pending.opts as ConfirmOptions).confirmLabel ?? 'Confirm'}
              </button>
            </>
          ) : (
            <button
              className="confirmPrimary"
              onClick={() => closeWith(undefined)}
            >
              {(pending.opts as AlertOptions).okLabel ?? 'OK'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
